import { DSSpacer } from '@zoe/ds-web'
import { useEffect } from 'react'

import type { SeoMetadata } from '@/types/datocms-schema'
import { LOCALE_GB, type LocaleSupportedPageRecord } from '@/types/locales'

import { useSuppressBrazeInAppMessages } from '@/context/brazeContext'
import { useLocale } from '@/context/localeContext'

import { Footer } from '@/components/elements/footer/Footer'
import { Header } from '@/components/elements/header/Header'
import { ProductHeroBlock, type ProductHeroRecord } from '@/components/patterns/PdpHero/ProductHeroBlock'
import { ProductSchema } from '@/components/shared/ProductSchema'
import { SEOMetaTags } from '@/components/shared/SEOMetaTags'

import { getCurrencyCodeForLocale } from '@/lib/checkout/utils'
import { trackGutHealthTestPDPViewEvent } from '@/lib/gtm/membershipGtm'
import { useAuthInfo } from '@/lib/hooks/useAuthInfo'
import { useSetTrackingSection } from '@/lib/hooks/useSetTrackingSection'
import { generateSEOAlternateUrls } from '@/lib/utils/localisationUtils'

import type { ModularContentBlocks } from '../renderModularBlocks'
import { GutHealthTestPDPContent } from './GutHealthTestPDPContent'

export interface GutHealthTestPDP extends LocaleSupportedPageRecord {
  /** The response key is `metaData` — a mismatch here silently drops every SEO tag. */
  metaData: SeoMetadata
  content: ModularContentBlocks[]
  /** The hero from the v2 model; optional only because draft records can miss it in preview. */
  hero?: ProductHeroRecord
}

export type GutHealthTestPDPProps = {
  page: GutHealthTestPDP
}

export const GutHealthTestPDPPage = ({ page }: GutHealthTestPDPProps) => {
  const { currentLocale } = useLocale()
  const { authenticatedUser, isLoading: isAuthLoading } = useAuthInfo()
  useSetTrackingSection('gut_health_test_pdp')
  useSuppressBrazeInAppMessages()

  useEffect(() => {
    if (isAuthLoading) return

    const currency = getCurrencyCodeForLocale(currentLocale)
    trackGutHealthTestPDPViewEvent({ currency, user_email: authenticatedUser?.email ?? undefined })
  }, [currentLocale, authenticatedUser, isAuthLoading])

  return (
    <>
      <SEOMetaTags
        metaData={page.metaData}
        alternateUrls={generateSEOAlternateUrls(`/buyguthealthtest`, [LOCALE_GB])}
      />
      <ProductSchema metadata={page.metaData} fallbackName="ZOE Gut Health Test" />

      <Header />

      {page.hero && (
        <>
          <DSSpacer direction="vertical" size={72} breakAt={{ m: 48 }} />
          <ProductHeroBlock record={page.hero} />
        </>
      )}
      <GutHealthTestPDPContent content={page.content} />
      <Footer />
    </>
  )
}
